import { fetchCurrentProject, fetchProject } from 'features/BillOfMaterials/store/asyncActions/fetchProject'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { WorkingStepWithData } from 'features/BomItemDetailsDialog/DetailsTabs/WorkingSteps/types/WorkingStepWithData'
import {
  AdditionalWorkingStepFormConfiguration,
  AdditionalWorkingStepFormModelBuilder,
} from 'model/AdditionalWorkingStep/AdditionalWorkingStepForm'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  AdditionalWorkingStepDto,
  BoMItemActivityDto,
  BoMItemIdAndActivityId,
  SetResourceDiscountRequest,
  SetResourceSurchargeRequest,
  UpdateWorkingStepsProductionOrderArgs,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { store } from 'store/configureStore'
import { GetContext } from 'store/storeUtils'
import { WorkingStepsAPI } from '../services/APIs/InternalAPI/WorkingStepsAPI'
import { APIFactory, BaseController } from './BaseController'
import { IWorkingStepsAPI } from './IWorkingStepsAPI'
import { BomItemController } from './Project/BomItemController'
import { ProjectResourceController } from './Project/ProjectResourceController'

export class WorkingStepsController extends BaseController<IWorkingStepsAPI> {
  static WorkingStepsOperations = {
    LOAD_DATA: 'WORKING_STEPS_LOAD_DATA',
    GetAvailableWorkingSteps: 'WORKING_STEPS_AVAILABLE_WORKING_STEPS',
    SAVE_DATA: 'WORKING_STEPS_SAVE_DATA',
    DELETE_DATA: 'WORKING_STEPS_DELETE_DATA',
    GET_FORM: 'WORKING_STEPS_GET_FORM',
    CHANGE_PRODUCTION_ORDER: 'WORKING_STEP_CHANGING_POSITION',
  } as const

  private projectId: string

  constructor(partyId?: string, apiFactory?: APIFactory<IWorkingStepsAPI>) {
    super(
      apiFactory
        ? apiFactory
        : (onRequestChange) =>
          new WorkingStepsAPI(
            partyId || store.getState().user?.organizationContext?.id,
            onRequestChange,
          ),
    )

    this.projectId = store.getState().project?.activeProject?.id
  }

  public async DeleteWorkingSteps(
    projectId: string,
    boMItemAndActivityIds: BoMItemIdAndActivityId[],
  ) {
    try {
      await this.api.DeleteWorkingSteps(projectId, boMItemAndActivityIds)
    } catch (ex) {
      throw this.HandleError(ex)
    }
  }

  async DeleteAdditionalWorkingStepData(
    projectId: string,
    boMItemAndActivityIds: BoMItemIdAndActivityId[],
    activityKey?: string,
  ) {
    try {
      await this.api.DeleteAdditionalWorkingStep(
        projectId,
        boMItemAndActivityIds,
        activityKey,
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async InsertWorkingSteps(
    projectId: string,
    bomItemPointers: BomItemPointer[],
    data: {
      workingStepType: WorkingStepType
      formData: Record<string, unknown>
      comment: string
    }[],
  ) {
    try {
      await Promise.all(
        data.map((x) =>
          this.api.InsertWorkingSteps(projectId, {
            bomItemIds: bomItemPointers.map((p) => p.id),
            workingStepType: x.workingStepType,
            formData: x.formData,
            comment: x.comment,
          }),
        ),
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async UpdateWorkingSteps(
    projectId: string,
    boMItemAndActivityIds: BoMItemIdAndActivityId[],
    data: {
      workingStepType: WorkingStepType
      formData: Record<string, unknown>
      comment: string
    }[],
  ) {
    try {
      await Promise.all(
        data.map((x) =>
          this.api.UpdateWorkingSteps(projectId, {
            bomItemAndActivityIds: boMItemAndActivityIds,
            workingStepType: x.workingStepType,
            formData: x.formData,
            comment: x.comment,
          }),
        ),
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  private GetProjectId(projectId: string | undefined) {
    if (projectId) {
      return projectId
    }

    const context = GetContext()
    if (!context.projectId) {
      throw new Error('Project ID is not defined')
    }

    return context.projectId
  }

  async GetWorkingSteps(projectId: string, bomItemId: string) {
    try {
      return await this.api.GetWorkingSteps(
        this.GetProjectId(projectId),
        bomItemId,
      )
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  async GetWorkingStepForm(
    projectId: string,
    bomItemPointer: BomItemPointer,
    activityId: string,
    workingStepType: WorkingStepType,
    isReloading?: boolean,
  ): Promise<AdditionalWorkingStepFormConfiguration> {
    try {
      if (!activityId) {
        return null;
      }

      const resp = await this.api.GetWorkingStepForm(
        this.GetProjectId(projectId),
        bomItemPointer.id,
        activityId,
        workingStepType,
        isReloading,
      )

      return resp
        ? AdditionalWorkingStepFormModelBuilder().FromAPIResponse(
          resp,
          workingStepType,
        )
        : null
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  private SanitizeWorkingStepsFormData(
    workingStepsWithData: WorkingStepWithData[],
  ) {
    return workingStepsWithData.map((x) => {
      if (x.formData === null) {
        return {
          ...x,
          formData: null,
        }
      }

      return {
        ...x,
        formData: Object.keys(x.formData || {}).reduce((acc, key) => {
          if (typeof x.formData[key] === 'string') {
            acc[key] = (x.formData[key] as string).trim()
          } else {
            acc[key] = x.formData[key]
          }

          return acc
        }, {}),
      }
    })
  }

  public async upInsertAndRemoveWorkingStepsWithData(
    bomItemPointers: BomItemPointer[],
    workingStepsToAdd: WorkingStepWithData[],
    workingStepsToRemove: Array<{
      activityId: string
      primaryWorkingStep?: WorkingStepType
    }>,
  ) {
    try {
      workingStepsToAdd = this.SanitizeWorkingStepsFormData(workingStepsToAdd)
      const shallRemovePurchasing =
        workingStepsToRemove?.findIndex(
          (x) => x.primaryWorkingStep === WorkingStepType.Purchasing,
        ) > -1

      if (shallRemovePurchasing) {
        const bomItemController = new BomItemController()
        bomItemController.UnsetAsPurchasingItem(bomItemPointers)
      }

      // update it locally only if its not purchasing related
      if (!shallRemovePurchasing) {
        bomItemPointers.forEach((pointer) => {
          workingStepsToRemove.forEach((ws) => {
            store.dispatch(
              newProjectActions.removeWorkingStep({
                bomItemPointer: pointer,
                activityId: ws.activityId,
              }),
            )
          })
        })
      }

      store.dispatch(newProjectActions.resetBomItemFinancials(bomItemPointers))

      // update it on the server
      const nonPurchasingToRemove = workingStepsToRemove?.filter(
        (x) => x.primaryWorkingStep !== WorkingStepType.Purchasing,
      )

      if (nonPurchasingToRemove?.length > 0) {
        const boMItemAndActivityIds: BoMItemIdAndActivityId[] = []
        bomItemPointers.forEach((pointer) => {
          nonPurchasingToRemove.forEach((ws) => {
            boMItemAndActivityIds.push({
              boMItemId: pointer.id,
              activityId: ws.activityId,
            })
          })
        })

        await this.DeleteWorkingSteps(this.projectId, boMItemAndActivityIds)
      }

      if (workingStepsToAdd?.length > 0) {
        const toInsert = workingStepsToAdd.filter((x) => !x.bomItemActivityId)
        const toUpdate = workingStepsToAdd.filter((x) => !!x.bomItemActivityId)

        if (toInsert.length > 0) {
          await this.InsertWorkingSteps(
            this.projectId,
            bomItemPointers,
            toInsert.map((x) => ({
              workingStepType: x.primaryWorkingStep,
              formData: x.formData,
              comment: x.comment,
            })),
          )
        }

        if (toUpdate.length > 0) {
          const boMItemAndActivityIds: BoMItemIdAndActivityId[] =
            bomItemPointers.flatMap((pointer) =>
              toUpdate.map((ws) => ({
                boMItemId: pointer.id,
                activityId: ws.bomItemActivityId,
              })),
            )

          await this.UpdateWorkingSteps(
            this.projectId,
            boMItemAndActivityIds,
            toUpdate.map((x) => ({
              workingStepType: x.primaryWorkingStep,
              formData: x.formData,
              comment: x.comment,
            })),
          )
        }
      }
    } catch (err) {
      store.dispatch(fetchProject({ projectId: this.projectId }))
      throw this.HandleError(err)
    }
  }

  public async handleSaveAndRemoveWorkingSteps(
    bomItemPointers: BomItemPointer[],
    additionalWorkingStepsData: {
      workingStep: BoMItemActivityDto
      formData: Record<string, unknown>
      isEmpty: boolean
      costDriveForm: AdditionalWorkingStepDto
      comment: string
    }[],
    workingStepsToRemove: Array<{
      activityId: string
      primaryWorkingStep?: WorkingStepType
    }>,
  ) {
    try {
      workingStepsToRemove?.forEach((ws) => {
        bomItemPointers.forEach((pointer) => {
          store.dispatch(
            newProjectActions.removeWorkingStep({
              bomItemPointer: pointer,
              activityId: ws.activityId,
            }),
          )
        })
      })

      additionalWorkingStepsData?.forEach((data) => {
        bomItemPointers.forEach((rowId) => {
          store.dispatch(
            newProjectActions.addWorkingStep({
              bomItemPointer: rowId,
              workingStep: data.workingStep,
            }),
          )
        })
      })

      store.dispatch(newProjectActions.refreshWorkingStepFilter())

      if (workingStepsToRemove?.length) {
        const boMItemAndActivityIds: BoMItemIdAndActivityId[] = []
        bomItemPointers.forEach((pointer) => {
          workingStepsToRemove.forEach((ws) => {
            boMItemAndActivityIds.push({
              boMItemId: pointer.id,
              activityId: ws.activityId,
            })
          })
        })

        await this.DeleteWorkingSteps(this.projectId, boMItemAndActivityIds)
      }

      if (additionalWorkingStepsData?.length) {
        await this.InsertWorkingSteps(
          this.projectId,
          bomItemPointers,
          additionalWorkingStepsData.map((x) => ({
            workingStepType: x.workingStep.primaryWorkingStep,
            formData: x.formData,
            comment: x.comment,
          })),
        )
      }
    } catch (err) {
      this.LogError(err)
      store.dispatch(fetchProject({ projectId: this.projectId }))
      throw err
    }
  }

  public async SetWorkingStepManualDiscount(
    resourceId: string,
    priceSummaryKey: string,
    args: SetResourceDiscountRequest,
  ) {
    try {
      const projectWorkingStepController = new ProjectResourceController(
        resourceId,
        priceSummaryKey,
        this.onRequestChange,
      )

      return await projectWorkingStepController.SetResourceDiscount({
        discountRatio: args.discountRatio,
        discountValue: args.discountValue,
      })
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async SetWorkingStepManualSurcharge(
    resourceId: string,
    priceSummaryKey: string,
    args: SetResourceSurchargeRequest,
  ) {
    try {
      const projectWorkingStepController = new ProjectResourceController(
        resourceId,
        priceSummaryKey,
        this.onRequestChange,
      )

      return await projectWorkingStepController.SetResourceSurcharge({
        surchargeRatio: args.surchargeRatio,
        surchargeValue: args.surchargeValue,
      })
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async CopyWorkingStepsToOtherParts(
    baseBomItemPointer: BomItemPointer,
    sourceActivityId: string,
    bomItemsToApply?: BomItemPointer[],
  ) {
    try {
      return await this.api.CopyWorkingStepsToOtherParts(this.projectId, {
        sourcePartTypeId: baseBomItemPointer.id,
        sourceActivityId,
        destinationPartTypeIds: bomItemsToApply.map((x) => x.id),
      })
    } catch (err) {
      this.LogError(err)
      throw err
    }
  }

  public async BatchUpdateWorkingStepsPosition(
    args: UpdateWorkingStepsProductionOrderArgs,
  ) {
    try {
      await this.api.BatchUpdateWorkingStepsPosition(this.projectId, args)
      store.dispatch(fetchProject({ projectId: this.projectId }))
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
